import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { invalidateStreamPaymentQueries } from "./queryInvalidation";

/**
 * Processes a stream payment between artist and sponsor using edge function
 * Amount: ₦50 per valid stream
 * Note: All amounts are stored in naira (main currency unit)
 */
export async function processStreamPayment(
  artistId: string,
  sponsorId: string,
  queryClient: any,
  trackId?: string,
): Promise<boolean> {
  try {
    const transactionRef = `stream-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`;

    const { data: amountResult, error: amountError } = await supabase.rpc(
      "get_stream_payment_amount",
      { p_artist_id: artistId },
    );
    const streamPaymentAmount =
      typeof amountResult === "number" && !amountError ? amountResult : 50; // naira fallback

    const { data, error } = await supabase.functions.invoke(
      "transfer-payment",
      {
        body: {
          artist_id: artistId,
          sponsor_id: sponsorId,
          track_id: trackId,
          reference: transactionRef,
          amount: streamPaymentAmount,
        },
      },
    );

    console.log("📡 [PAYMENT_PROCESSOR] Edge function response:", {
      data,
      error,
      hasData: !!data,
      hasError: !!error,
      timestamp: new Date().toISOString(),
    });

    if (error) {
      console.error(
        "❌ [PAYMENT_PROCESSOR] Stream payment edge function error:",
        {
          error,
          errorMessage: error.message,
          errorDetails: error.details,
          artistId,
          sponsorId,
          transactionRef,
          amount_naira: streamPaymentAmount,
        },
      );
      return false;
    }

    if (!data || !data.success) {
      console.error("❌ [PAYMENT_PROCESSOR] Stream payment failed:", {
        data,
        error: data?.error || "Unknown error",
        artistId,
        sponsorId,
        transactionRef,
        amount_naira: streamPaymentAmount,
      });
      return false;
    }

    console.log(
      "✅ [PAYMENT_PROCESSOR] Stream payment processed successfully:",
      {
        transaction: data.transaction,
        amount_processed_naira: data.transaction?.amount || streamPaymentAmount,
        artist_balance_naira: data.transaction?.artist_balance,
        sponsor_balance_naira: data.transaction?.sponsor_balance,
        artistId,
        sponsorId,
        transactionRef,
        timestamp: new Date().toISOString(),
      },
    );

    // Invalidate related queries to refresh UI
    console.log(
      "🔄 [PAYMENT_PROCESSOR] Invalidating queries after successful payment",
    );
    invalidateStreamPaymentQueries(queryClient, artistId, sponsorId);

    return true;
  } catch (error) {
    console.error(
      "🔥 [PAYMENT_PROCESSOR] Unhandled exception in processStreamPayment:",
      {
        error,
        errorMessage: error instanceof Error ? error.message : "Unknown error",
        errorStack: error instanceof Error ? error.stack : undefined,
        artistId,
        sponsorId,
      },
    );
    return false;
  }
}
